import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MapPin, Star, Navigation, MessageCircle, Map, ChevronRight } from 'lucide-react';
import Button from './Button';
import { getImageUrl } from '../utils/getImageUrl';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { fetchEstablishmentReviews, reviewAuthorDisplayName, type ReviewWithAuthor } from '../utils/reviewDisplay';
import type { GeoPoint } from '../utils/distance';
import { distanceLabelFromCoords } from '../utils/distance';
import type { EstablishmentMapPoint } from '../hooks/useData';
import './MapBottomSheet.css';

interface MapBottomSheetProps {
    /** Só monte o componente quando houver local selecionado (evita violação das regras de hooks). */
    place: EstablishmentMapPoint;
    /** GPS do usuário (watch no mapa) para exibir distância em linha reta ao local. */
    userLocation?: GeoPoint | null;
    onClose: () => void;
    /** Caminho a pé no mapa do app (localização + OSRM walking). */
    onRouteInApp?: (dest: { lat: number; lng: number }) => void | Promise<void>;
}

function formatReviewDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function ReviewStars({ count }: { count: number }) {
    return (
        <div className="map-place-modal-stars" aria-hidden>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    size={14}
                    fill={i <= count ? '#FFD700' : 'transparent'}
                    color="#FFD700"
                />
            ))}
        </div>
    );
}

function buildDirectionsUrls(lat: number, lng: number) {
    const ll = `${lat},${lng}`;
    return {
        google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
        waze: `https://waze.com/ul?ll=${encodeURIComponent(ll)}&navigate=yes`,
        apple: `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`,
    };
}

const MapBottomSheet = ({ place, userLocation = null, onClose, onRouteInApp }: MapBottomSheetProps) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [reviews, setReviews] = useState<ReviewWithAuthor[]>([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setReviewsLoading(true);

        (async () => {
            try {
                const viewer =
                    user != null
                        ? { id: user.id, name: user.name, avatar: user.avatar ?? null }
                        : undefined;
                const rows = await fetchEstablishmentReviews(supabase, place.id, {
                    limit: 25,
                    viewer,
                });
                if (!cancelled) setReviews(rows);
            } catch {
                if (!cancelled) setReviews([]);
            } finally {
                if (!cancelled) setReviewsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [place.id, user]);

    useEffect(() => {
        setDirectionsOpen(false);
    }, [place.id]);

    const distanceLabel = useMemo(
        () => distanceLabelFromCoords(userLocation, place.lat, place.lng, place.distance),
        [userLocation, place],
    );

    const urls = buildDirectionsUrls(place.lat, place.lng);

    const openUrl = (href: string) => {
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const handleInAppRoute = async () => {
        if (!onRouteInApp) return;
        setDirectionsOpen(false);
        await Promise.resolve(onRouteInApp({ lat: place.lat, lng: place.lng }));
    };

    return (
        <div className="map-place-modal-overlay" onClick={onClose} role="presentation">
            <div
                className="map-place-modal"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="map-place-modal-title"
            >
                <button type="button" className="map-place-modal-close" onClick={onClose} aria-label="Fechar">
                    <X size={18} />
                </button>

                <div className="map-place-modal-image">
                    <img src={getImageUrl(place.image)} alt={place.name} />
                    {place.discount && (
                        <span className="map-place-modal-badge">{place.discount}</span>
                    )}
                </div>

                <div className="map-place-modal-scroll">
                    <div className="map-place-modal-body">
                        <h3 id="map-place-modal-title">{place.name}</h3>
                        <div className="map-place-modal-meta">
                            {place.rating != null && place.rating > 0 && (
                                <span className="meta-tag">
                                    <Star size={13} fill="#FFD93D" color="#FFD93D" />
                                    {place.rating}
                                </span>
                            )}
                            {distanceLabel !== '—' && (
                                <span className="meta-tag">
                                    <MapPin size={13} /> {distanceLabel}
                                </span>
                            )}
                            {place.category?.label && (
                                <span className="meta-tag">{place.category.label}</span>
                            )}
                        </div>
                    </div>

                    <section className="map-place-modal-reviews" aria-label="Comentários">
                        <h4 className="map-place-modal-reviews-title">
                            <MessageCircle size={17} strokeWidth={2} />
                            Comentários
                            {!reviewsLoading && reviews.length > 0 && (
                                <span className="map-place-modal-reviews-count">({reviews.length})</span>
                            )}
                        </h4>

                        {reviewsLoading ? (
                            <p className="map-place-modal-reviews-hint">Carregando comentários…</p>
                        ) : reviews.length === 0 ? (
                            <p className="map-place-modal-reviews-empty">Ainda não há comentários neste local.</p>
                        ) : (
                            <ul className="map-place-modal-review-list">
                                {reviews.map(review => (
                                    <li key={review.id} className="map-place-modal-review-card">
                                        <div className="map-place-modal-review-head">
                                            <span className="map-place-modal-review-user">
                                                {reviewAuthorDisplayName(review)}
                                            </span>
                                            <ReviewStars count={review.rating} />
                                        </div>
                                        {review.comment ? (
                                            <p className="map-place-modal-review-text">{review.comment}</p>
                                        ) : (
                                            <p className="map-place-modal-review-text map-place-modal-review-muted">
                                                Avaliou sem comentário.
                                            </p>
                                        )}
                                        <time
                                            className="map-place-modal-review-date"
                                            dateTime={review.createdAt}
                                        >
                                            {formatReviewDate(review.createdAt)}
                                        </time>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                <div className="map-place-modal-actions">
                    <Button
                        variant="primary"
                        onClick={() => navigate(`/establishment/${place.id}`)}
                        style={{ flex: 1 }}
                    >
                        Ver detalhes
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => setDirectionsOpen(true)}
                        style={{ flex: 1 }}
                    >
                        <Navigation size={15} /> Como chegar
                    </Button>
                </div>
            </div>

            {directionsOpen && (
                <div
                    className="map-directions-backdrop"
                    role="presentation"
                    onClick={e => {
                        e.stopPropagation();
                        setDirectionsOpen(false);
                    }}
                >
                    <div
                        className="map-directions-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="map-directions-title"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="map-directions-panel-handle" aria-hidden />
                        <h4 id="map-directions-title" className="map-directions-title">
                            Como chegar
                        </h4>
                        <p className="map-directions-subtitle">Escolha como deseja navegar até {place.name}</p>

                        <ul className="map-directions-options">
                            {onRouteInApp && (
                                <li>
                                    <button
                                        type="button"
                                        className="map-directions-option"
                                        onClick={() => void handleInAppRoute()}
                                    >
                                        <span className="map-directions-option-icon map-directions-option-icon--app">
                                            <Map size={20} strokeWidth={2} />
                                        </span>
                                        <span className="map-directions-option-text">
                                            <strong>Caminho a pé no mapa</strong>
                                            <small>Trajeto pedestre no app (sua localização)</small>
                                        </span>
                                        <ChevronRight size={18} className="map-directions-option-chevron" />
                                    </button>
                                </li>
                            )}
                            <li>
                                <button
                                    type="button"
                                    className="map-directions-option"
                                    onClick={() => openUrl(urls.google)}
                                >
                                    <span className="map-directions-option-icon map-directions-option-icon--google">
                                        G
                                    </span>
                                    <span className="map-directions-option-text">
                                        <strong>Google Maps</strong>
                                        <small>Abrir no app ou no navegador</small>
                                    </span>
                                    <ChevronRight size={18} className="map-directions-option-chevron" />
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="map-directions-option"
                                    onClick={() => openUrl(urls.waze)}
                                >
                                    <span className="map-directions-option-icon map-directions-option-icon--waze">
                                        W
                                    </span>
                                    <span className="map-directions-option-text">
                                        <strong>Waze</strong>
                                        <small>Navegação com trânsito</small>
                                    </span>
                                    <ChevronRight size={18} className="map-directions-option-chevron" />
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="map-directions-option"
                                    onClick={() => openUrl(urls.apple)}
                                >
                                    <span className="map-directions-option-icon map-directions-option-icon--apple">
                                        A
                                    </span>
                                    <span className="map-directions-option-text">
                                        <strong>Apple Maps</strong>
                                        <small>Ideal no iPhone e iPad</small>
                                    </span>
                                    <ChevronRight size={18} className="map-directions-option-chevron" />
                                </button>
                            </li>
                        </ul>

                        <button
                            type="button"
                            className="map-directions-cancel"
                            onClick={() => setDirectionsOpen(false)}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapBottomSheet;
